import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Phone, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SECTION_IDS, useLang } from "@/lib/navigation";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const ContactForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = useLang();
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (values.name.trim().length < 2) next.name = t("contact.errors.name");
    if (!EMAIL_RE.test(values.email.trim())) next.email = t("contact.errors.email");
    if (values.message.trim().length < 10) next.message = t("contact.errors.message");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    toast.success(t("contact.success_toast"));
    window.setTimeout(() => navigate("/thank_you"), 450);
  };

  return (
    <section
      id={SECTION_IDS.contact}
      className="bg-background"
      data-testid="home-contact"
    >
      <div className="container-page py-16 sm:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">{t("contact.eyebrow")}</p>
            <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-[34px]">
              {t("contact.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {t("contact.lead")}
            </p>

            <div className="mt-7 rounded-xl border border-border bg-[hsl(var(--surface-2))] p-5">
              <h3 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
                {t("contact.aside_title")}
              </h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-2.5">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--terracotta))]" />
                  <a
                    href={`mailto:${t("footer.email")}?subject=Consulta%20Litiosur`}
                    className="text-[hsl(var(--ink-2))] underline-offset-4 transition-colors hover:text-[hsl(var(--terracotta))] hover:underline"
                    data-testid="contact-email-link"
                  >
                    {t("footer.email")}
                  </a>
                </li>
                <li className="flex items-start gap-2.5">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--terracotta))]" />
                  <a
                    href={`tel:${t("footer.phone").replace(/\s/g, "")}`}
                    className="text-[hsl(var(--ink-2))] underline-offset-4 transition-colors hover:text-[hsl(var(--terracotta))] hover:underline"
                    data-testid="contact-phone-link"
                  >
                    {t("footer.phone")}
                  </a>
                </li>
                <li className="flex items-start gap-2.5">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--mineral-gray))]" />
                  <span className="text-muted-foreground">
                    {t("contact.aside_hours")}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="rounded-xl border border-border bg-card p-6 sm:p-8"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contact-name" className="text-sm">
                    {t("contact.name_label")}
                  </Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={values.name}
                    onChange={setField("name")}
                    placeholder={t("contact.name_placeholder")}
                    className="mt-2 h-11 bg-background"
                    aria-invalid={Boolean(errors.name)}
                    data-testid="contact-form-name-input"
                  />
                  {errors.name && (
                    <p
                      className="mt-1.5 text-xs text-destructive"
                      data-testid="contact-form-name-error"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-sm">
                    {t("contact.email_label")}
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={setField("email")}
                    placeholder={t("contact.email_placeholder")}
                    className="mt-2 h-11 bg-background"
                    aria-invalid={Boolean(errors.email)}
                    data-testid="contact-form-email-input"
                  />
                  {errors.email && (
                    <p
                      className="mt-1.5 text-xs text-destructive"
                      data-testid="contact-form-email-error"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="contact-message" className="text-sm">
                  {t("contact.message_label")}
                </Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  value={values.message}
                  onChange={setField("message")}
                  placeholder={t("contact.message_placeholder")}
                  className="mt-2 resize-y bg-background"
                  aria-invalid={Boolean(errors.message)}
                  data-testid="contact-form-message-textarea"
                />
                {errors.message && (
                  <p
                    className="mt-1.5 text-xs text-destructive"
                    data-testid="contact-form-message-error"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-12 rounded-lg px-6 text-sm"
                  data-testid="contact-form-submit-button"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {submitting ? t("contact.sending") : t("contact.submit")}
                </Button>
                <p className="max-w-sm text-[11px] leading-relaxed text-muted-foreground">
                  {t("contact.note")}{" "}
                  <Link
                    to={`/${lang}/privacy-policy`}
                    className="font-medium text-[hsl(var(--deep-blue))] underline underline-offset-2"
                    data-testid="contact-form-privacy-link"
                  >
                    {t("nav.privacy")}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
